import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  serverTimestamp,
  query,
  where
} from 'firebase/firestore';
import { db } from '../config/firebase';

export class CategoryService {
  static collectionName = 'categories';
  static CATEGORY_SECTIONS = {
    CARDS: 'cards',
    TOPUP: 'topup'
  };
  static CATEGORY_TYPES = {
    CATEGORY: 'category',
    SUBCATEGORY: 'subcategory'
  };

  static normalizeSection(section) {
    return section === this.CATEGORY_SECTIONS.TOPUP
      ? this.CATEGORY_SECTIONS.TOPUP
      : this.CATEGORY_SECTIONS.CARDS;
  }

  static normalizeCategoryType(type) {
    return type === this.CATEGORY_TYPES.SUBCATEGORY
      ? this.CATEGORY_TYPES.SUBCATEGORY
      : this.CATEGORY_TYPES.CATEGORY;
  }

  static matchesSection(category = {}, section = this.CATEGORY_SECTIONS.CARDS) {
    const normalizedSection = this.normalizeSection(section);
    const safeCategory = category && typeof category === 'object' ? category : {};
    const categorySection = this.normalizeSection(safeCategory.section);
    return categorySection === normalizedSection;
  }

  static normalizeCategoryDocument(docSnapshot) {
    const data = docSnapshot.data() || {};
    const normalizedSection = this.normalizeSection(data.section);
    const normalizedType = normalizedSection === this.CATEGORY_SECTIONS.TOPUP
      ? this.normalizeCategoryType(data.type)
      : this.CATEGORY_TYPES.CATEGORY;

    return {
      id: docSnapshot.id,
      ...data,
      section: normalizedSection,
      type: normalizedType,
      parentCategoryId: String(data.parentCategoryId || '').trim(),
      parentCategoryName: String(data.parentCategoryName || '').trim()
    };
  }

  static isTopupSubcategory(category = {}) {
    if (!category || typeof category !== 'object') {
      return false;
    }

    return this.matchesSection(category, this.CATEGORY_SECTIONS.TOPUP) &&
      this.normalizeCategoryType(category.type) === this.CATEGORY_TYPES.SUBCATEGORY;
  }

  static isTopupParentCategory(category = {}) {
    if (!category || typeof category !== 'object') {
      return false;
    }

    return this.matchesSection(category, this.CATEGORY_SECTIONS.TOPUP) &&
      !this.isTopupSubcategory(category);
  }

  /**
   * Récupérer toutes les catégories
   */
  static async getAllCategories(section = null) {
    try {
      const q = query(
        collection(db, this.collectionName)
      );
      const querySnapshot = await getDocs(q);
      const categories = querySnapshot.docs.map((docSnapshot) => this.normalizeCategoryDocument(docSnapshot));
      const filteredCategories = section
        ? categories.filter((category) => this.matchesSection(category, section))
        : categories;
      
      // Trier côté client pour éviter l'index
      return filteredCategories.sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      console.error('Erreur lors de la récupération des catégories:', error);
      throw error;
    }
  }

  /**
   * Récupérer les catégories actives
   */
  static async getActiveCategories(section = null) {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('status', '==', 'active')
      );
      const querySnapshot = await getDocs(q);
      const categories = querySnapshot.docs.map((docSnapshot) => this.normalizeCategoryDocument(docSnapshot));
      const filteredCategories = section
        ? categories.filter((category) => this.matchesSection(category, section))
        : categories;
      
      // Trier côté client pour éviter l'index composite
      return filteredCategories.sort((a, b) => a.name.localeCompare(b.name));
    } catch (error) {
      console.error('Erreur lors de la récupération des catégories actives:', error);
      throw error;
    }
  }

  /**
   * Récupérer une catégorie par ID
   */
  static async getCategoryById(id) {
    try {
      const docRef = doc(db, this.collectionName, id);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        return this.normalizeCategoryDocument(docSnap);
      } else {
        throw new Error('Catégorie non trouvée');
      }
    } catch (error) {
      console.error('Erreur lors de la récupération de la catégorie:', error);
      throw error;
    }
  }

  /**
   * Créer une nouvelle catégorie
   */
  static async createCategory(categoryData) {
    try {
      const normalizedSection = this.normalizeSection(categoryData.section);
      const normalizedType = normalizedSection === this.CATEGORY_SECTIONS.TOPUP
        ? this.normalizeCategoryType(categoryData.type)
        : this.CATEGORY_TYPES.CATEGORY;

      const docRef = await addDoc(collection(db, this.collectionName), {
        ...categoryData,
        section: normalizedSection,
        type: normalizedType,
        parentCategoryId: normalizedType === this.CATEGORY_TYPES.SUBCATEGORY
          ? String(categoryData.parentCategoryId || '').trim()
          : '',
        parentCategoryName: normalizedType === this.CATEGORY_TYPES.SUBCATEGORY
          ? String(categoryData.parentCategoryName || '').trim()
          : '',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return docRef.id;
    } catch (error) {
      console.error('Erreur lors de la création de la catégorie:', error);
      throw error;
    }
  }

  /**
   * Mettre à jour une catégorie
   */
  static async updateCategory(id, categoryData) {
    try {
      const docRef = doc(db, this.collectionName, id);
      const existingCategory = await this.getCategoryById(id);
      const mergedCategoryData = {
        ...existingCategory,
        ...categoryData
      };
      const normalizedSection = this.normalizeSection(mergedCategoryData.section);
      const normalizedType = normalizedSection === this.CATEGORY_SECTIONS.TOPUP
        ? this.normalizeCategoryType(mergedCategoryData.type)
        : this.CATEGORY_TYPES.CATEGORY;

      await updateDoc(docRef, {
        ...mergedCategoryData,
        section: normalizedSection,
        type: normalizedType,
        parentCategoryId: normalizedType === this.CATEGORY_TYPES.SUBCATEGORY
          ? String(mergedCategoryData.parentCategoryId || '').trim()
          : '',
        parentCategoryName: normalizedType === this.CATEGORY_TYPES.SUBCATEGORY
          ? String(mergedCategoryData.parentCategoryName || '').trim()
          : '',
        updatedAt: serverTimestamp()
      });
      
      return true;
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la catégorie:', error);
      throw error;
    }
  }

  /**
   * Supprimer une catégorie
   */
  static async deleteCategory(id) {
    try {
      const docRef = doc(db, this.collectionName, id);
      await deleteDoc(docRef);
      return true;
    } catch (error) {
      console.error('Erreur lors de la suppression de la catégorie:', error);
      throw error;
    }
  }

  /**
   * Vérifier si une catégorie existe par nom
   */
  static async categoryExistsByName(name, excludeId = null, section = this.CATEGORY_SECTIONS.CARDS, options = {}) {
    try {
      const categories = await this.getAllCategories(section);
      const normalizedName = name.trim().toLowerCase();
      const normalizedType = this.normalizeSection(section) === this.CATEGORY_SECTIONS.TOPUP
        ? this.normalizeCategoryType(options.type)
        : this.CATEGORY_TYPES.CATEGORY;
      const normalizedParentCategoryId = String(options.parentCategoryId || '').trim();
      
      if (excludeId) {
        return categories.some((category) => (
          category.id !== excludeId &&
          this.normalizeCategoryType(category.type) === normalizedType &&
          String(category.parentCategoryId || '').trim() === normalizedParentCategoryId &&
          category.name?.trim().toLowerCase() === normalizedName
        ));
      }
      
      return categories.some((category) => (
        this.normalizeCategoryType(category.type) === normalizedType &&
        String(category.parentCategoryId || '').trim() === normalizedParentCategoryId &&
        category.name?.trim().toLowerCase() === normalizedName
      ));
    } catch (error) {
      console.error('Erreur lors de la vérification de la catégorie:', error);
      throw error;
    }
  }

  /**
   * Basculer le statut d'une catégorie (active/inactive)
   */
  static async toggleCategoryStatus(categoryId) {
    try {
      const category = await this.getCategoryById(categoryId);
      const newStatus = category.status === 'active' ? 'inactive' : 'active';
      
      await this.updateCategory(categoryId, { status: newStatus });
      
      console.log('✅ Statut de la catégorie basculé:', categoryId, 'vers', newStatus);
      return newStatus;
    } catch (error) {
      console.error('❌ Erreur lors du basculement du statut:', error);
      throw error;
    }
  }

  /**
   * Récupérer les catégories avec le nombre de produits associés
   */
  static async getCategoriesWithProductCount(section = this.CATEGORY_SECTIONS.CARDS) {
    try {
      const normalizedSection = this.normalizeSection(section);
      const categories = await this.getAllCategories(normalizedSection);
      
      // Importer ProductService ici pour éviter les dépendances circulaires
      const { ProductService } = await import('./productService');
      const products = await ProductService.getAllProducts();
      const categoriesWithCount = categories.map((category) => {
        const productCount = products.filter((product) => {
          if (normalizedSection === this.CATEGORY_SECTIONS.TOPUP && this.isTopupSubcategory(category)) {
            return (
              (product.subcategoryId && product.subcategoryId === category.id) ||
              (
                product.subcategoryName &&
                this.normalizeCategory(product.subcategoryName) === this.normalizeCategory(category.name)
              )
            );
          }

          return (
            (product.categoryId && product.categoryId === category.id) ||
            (
              product.category &&
              this.normalizeCategory(product.category) === this.normalizeCategory(category.name)
            )
          );
        }).length;

        return {
          ...category,
          productCount,
          subcategoryCount: normalizedSection === this.CATEGORY_SECTIONS.TOPUP && this.isTopupParentCategory(category)
            ? categories.filter((entry) => String(entry.parentCategoryId || '').trim() === category.id).length
            : 0
        };
      });
      
      return categoriesWithCount;
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des catégories avec comptage:', error);
      throw error;
    }
  }

  /**
   * Normaliser le nom de catégorie pour créer un ID
   */
  static normalizeCategory(category) {
    if (!category) return 'autres';
    return category
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
  }

  static async getTopupParentCategories(activeOnly = false) {
    const categories = activeOnly
      ? await this.getActiveCategories(this.CATEGORY_SECTIONS.TOPUP)
      : await this.getAllCategories(this.CATEGORY_SECTIONS.TOPUP);

    return categories.filter((category) => this.isTopupParentCategory(category));
  }

  static async getTopupSubcategories(parentCategoryId = '', activeOnly = false) {
    const categories = activeOnly
      ? await this.getActiveCategories(this.CATEGORY_SECTIONS.TOPUP)
      : await this.getAllCategories(this.CATEGORY_SECTIONS.TOPUP);

    return categories.filter((category) => (
      this.isTopupSubcategory(category) &&
      (!parentCategoryId || String(category.parentCategoryId || '').trim() === String(parentCategoryId).trim())
    ));
  }
}
